import { useState, useEffect, useCallback } from "react";
import { getSprintResumo, getSprintDevs } from "../../api/sprint.api";
import { getProjetosCompleto } from "../../api/projetos.api";


/* ─── Helpers ─────────────────────────────────────────────────────── */

function getCategoriaTarefa(t) {
  if (t.status === "Concluida") return null;
  if (!t.data_entrega) return "sem_prazo";
  const hoje = new Date(new Date().toDateString());
  const diff = Math.floor((new Date(t.data_entrega) - hoje) / 86400000);
  if (diff < 0)  return "atrasada";
  if (diff <= 1) return "hoje";
  if (diff <= 7) return "em7dias";
  return "ok";
}

function pctColor(v) {
  if (v >= 70) return "#4ade80";
  if (v >= 40) return "#fbbf24";
  return "#f87171";
}

const TICKET_FECHADOS = new Set([
  "Fechado","Resolvido","Em Teste","Feedback",
  "Aguardando Testes","Ajuste Especificação","Aguardando Versão",
]);
const isTicketFechado = t => t.ticket_fechado || TICKET_FECHADOS.has(t.ticket_status);

const GP_STATUS = {
  1:  { label: "Em dia",       color: "#4ade80" },
  2:  { label: "Não iniciado", color: "#94a3b8" },
  3:  { label: "Parado",       color: "#f87171" },
  4:  { label: "Em Atraso",    color: "#f87171" },
  14: { label: "Em andamento", color: "#60a5fa" },
  16: { label: "Finalizado",   color: "#4ade80" },
  17: { label: "Restrição",    color: "#fb923c" },
  18: { label: "Em Piloto",    color: "#22d3ee" },
};
const gpStatus = id => GP_STATUS[id] ?? { label: "—", color: "#334155" };

/* ─── Página ─────────────────────────────────────────────────────── */

export default function DashboardExecutivo() {
  const [sprint,   setSprint]   = useState(null);
  const [devs,     setDevs]     = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [tarefas,  setTarefas]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [ts,       setTs]       = useState("");

  const load = useCallback(async (bust = false) => {
    try {
      const [resumo, devsData, completo] = await Promise.all([
        getSprintResumo(),
        getSprintDevs(),
        getProjetosCompleto(bust, 2),
      ]);
      const projetosRows = completo?.projetos || [];
      setSprint(resumo);
      setDevs(devsData.developers || []);

      // Projetos raiz (tipo=2 sem pai)
      const projMap = {};
      projetosRows.forEach(r => {
        if (!projMap[r.id_projeto]) {
          projMap[r.id_projeto] = {
            id: r.id_projeto, descricao: r.descricao, nivel: r.nivel,
            id_gp_status: r.id_gp_status, id_projeto_pai: r.id_projeto_pai,
            importancia: r.importancia, deadline: r.deadline,
            tickets: [],
          };
        }
        if (r.ticketid) projMap[r.id_projeto].tickets.push(r);
      });
      const raizes = Object.values(projMap)
        .filter(p => !p.id_projeto_pai)
        .sort((a, b) => (b.importancia || 0) - (a.importancia || 0));
      setProjetos(raizes);
      setTarefas(completo?.tarefas || []);
      setTs(new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh 60s — usa bust=true para sempre buscar dados frescos
  useEffect(() => {
    const id = setInterval(() => load(true), 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <div style={skeletonPage}><span style={{ color: "#475569" }}>Carregando dashboard...</span></div>;

  /* ── Métricas de sprint ── */
  const sprintTotal   = sprint?.saida?.geral?.total    || 0;
  const sprintFechado = sprint?.saida?.geral?.fechado  || 0;
  const sprintAberto  = sprint?.saida?.geral?.aberto   || 0;
  const sprintPct     = sprintTotal > 0 ? Math.round(sprintFechado / sprintTotal * 100) : 0;
  const devsComAlerta = devs.filter(d => {
    const total  = (d.programado?.total || 0) + (d.emergencia?.total || 0);
    const fech   = (d.programado?.fechado || 0) + (d.emergencia?.fechado || 0);
    return total > 0 && Math.round(fech / total * 100) < 40;
  });

  /* ── Métricas de tarefas ── */
  const tAtrasadas  = tarefas.filter(t => getCategoriaTarefa(t) === "atrasada").length;
  const tHoje       = tarefas.filter(t => getCategoriaTarefa(t) === "hoje").length;
  const tBloqueadas = tarefas.filter(t => t.status === "Bloqueada").length;
  const tSemPrazo   = tarefas.filter(t => getCategoriaTarefa(t) === "sem_prazo").length;
  const tAbertas    = tarefas.filter(t => t.status !== "Concluida").length;
  const tConcluidas = tarefas.filter(t => t.status === "Concluida").length;
  const tPct        = tarefas.length > 0 ? Math.round(tConcluidas / tarefas.length * 100) : 0;

  /* ── Velocidade do time por responsável ── */
  const hoje = new Date(new Date().toDateString());
  const respMap = {};
  tarefas.forEach(t => {
    const nome = t.responsavel || "Sem responsável";
    if (!respMap[nome]) respMap[nome] = { nome, total: 0, concluidas: 0, atrasadas: 0, noprazo: 0 };
    respMap[nome].total++;
    if (t.status === "Concluida") {
      respMap[nome].concluidas++;
      if (t.data_entrega && t.data_entrega_real) {
        const prazo = new Date(t.data_entrega);
        const real  = new Date(t.data_entrega_real);
        if (real <= prazo) respMap[nome].noprazo++;
      }
    } else if (t.data_entrega && new Date(t.data_entrega) < hoje) {
      respMap[nome].atrasadas++;
    }
  });
  const velocidade = Object.values(respMap)
    .filter(r => r.nome !== "Sem responsável" && r.total > 0)
    .sort((a, b) => b.concluidas - a.concluidas);

  /* ── Métricas de projetos ── */
  const projEmAtraso  = projetos.filter(p => p.id_gp_status === 4  || p.id_gp_status === 3).length;
  const projFinalizados = projetos.filter(p => p.id_gp_status === 16).length;
  const projAtivos    = projetos.filter(p => ![16, 2].includes(p.id_gp_status)).length;

  function exportarPDF() {
    const hoje = new Date().toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });
    const hoje_dt = new Date(new Date().toDateString());
    const criticas = tarefas.filter(t => {
      if (t.status === "Concluida" || !t.data_entrega) return false;
      return Math.floor((new Date(t.data_entrega) - hoje_dt) / 864e5) <= 1;
    }).slice(0, 15);

    const devRows = devs.map((d, i) => {
      const tot = (d.programado?.total||0) + (d.emergencia?.total||0);
      const fec = (d.programado?.fechado||0) + (d.emergencia?.fechado||0);
      const abe = (d.programado?.aberto||0)  + (d.emergencia?.aberto||0);
      const pct = tot ? Math.round(fec / tot * 100) : 0;
      const cor = pct >= 70 ? "#166534;background:#dcfce7" : pct >= 40 ? "#92400e;background:#fef3c7" : "#991b1b;background:#fee2e2";
      return `<tr>
        <td style="color:#94a3b8;font-weight:700;padding:7px 10px">${i + 1}</td>
        <td style="font-weight:600;padding:7px 10px">${d.nome}</td>
        <td style="text-align:center;padding:7px 10px">${tot}</td>
        <td style="text-align:center;color:#15803d;font-weight:700;padding:7px 10px">${fec}</td>
        <td style="text-align:center;color:#b45309;font-weight:700;padding:7px 10px">${abe}</td>
        <td style="text-align:center;padding:7px 10px"><span style="padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;color:${cor}">${pct}%</span></td>
      </tr>`;
    }).join("");

    const projRows = projetos.map(p => {
      const tot = p.tickets?.length || 0;
      const fec = p.tickets?.filter(t => t.ticket_fechado || t.done_ratio >= 100).length || 0;
      const pct = tot ? Math.round(fec / tot * 100) : 0;
      const dl  = p.deadline;
      const dlStr = dl ? new Date(dl).toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric" }) : "—";
      const dlCor = dl && new Date(dl) < new Date() ? "color:#991b1b;font-weight:700" : "color:#475569";
      const cor = pct >= 70 ? "#166534;background:#dcfce7" : pct >= 40 ? "#92400e;background:#fef3c7" : "#991b1b;background:#fee2e2";
      return `<tr>
        <td style="font-weight:600;padding:7px 10px">${p.descricao}</td>
        <td style="text-align:center;padding:7px 10px">${tot}</td>
        <td style="text-align:center;color:#15803d;padding:7px 10px">${fec}</td>
        <td style="text-align:center;padding:7px 10px"><span style="padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;color:${cor}">${pct}%</span></td>
        <td style="padding:7px 10px;${dlCor}">${dlStr}</td>
      </tr>`;
    }).join("");

    const criticasRows = criticas.map(t => {
      const diff  = Math.floor((new Date(t.data_entrega) - hoje_dt) / 864e5);
      const label = diff < 0 ? `${Math.abs(diff)}d atraso` : diff === 0 ? "Hoje" : "Amanhã";
      const cor   = diff < 0 ? "color:#991b1b" : "color:#92400e";
      return `<tr>
        <td style="padding:7px 10px">${t.descritivo}</td>
        <td style="padding:7px 10px">${t.responsavel || "—"}</td>
        <td style="padding:7px 10px"><span style="padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;color:#1e40af;background:#dbeafe">${t.status}</span></td>
        <td style="padding:7px 10px;font-weight:700;${cor}">${label}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Dashboard Executivo — ${hoje}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;padding:30px;color:#1e293b;font-size:12px}
        h1{font-size:22px;font-weight:800;color:#0f172a;margin-bottom:4px}
        .sub{color:#64748b;font-size:11px;margin-bottom:24px}
        .sec{margin-bottom:22px}
        .sec-title{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#64748b;border-bottom:2px solid #e2e8f0;padding-bottom:6px;margin-bottom:14px}
        .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:22px}
        .kpi{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;text-align:center}
        .kpi-l{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px}
        .kpi-v{font-size:26px;font-weight:800}
        table{width:100%;border-collapse:collapse}
        th{background:#f1f5f9;text-align:left;padding:7px 10px;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#475569;font-weight:700}
        td{border-bottom:1px solid #f1f5f9;font-size:11px}
        .footer{margin-top:30px;border-top:1px solid #e2e8f0;padding-top:12px;color:#94a3b8;font-size:10px;text-align:center}
        @media print{body{padding:15px}}
      </style></head><body>
      <h1>Dashboard Executivo</h1>
      <div class="sub">Gerado em ${hoje}</div>
      <div class="kpis">
        <div class="kpi"><div class="kpi-l">Tickets</div><div class="kpi-v" style="color:#3b82f6">${sprintTotal}</div></div>
        <div class="kpi"><div class="kpi-l">Fechados</div><div class="kpi-v" style="color:#22c55e">${sprintFechado}</div></div>
        <div class="kpi"><div class="kpi-l">Abertos</div><div class="kpi-v" style="color:#f59e0b">${sprintAberto}</div></div>
        <div class="kpi"><div class="kpi-l">Conclusão</div><div class="kpi-v" style="color:${sprintPct>=70?"#22c55e":sprintPct>=40?"#f59e0b":"#ef4444"}">${sprintPct}%</div></div>
      </div>
      ${devRows ? `<div class="sec"><div class="sec-title">Desenvolvedores</div><table><thead><tr><th>#</th><th>Nome</th><th style="text-align:center">Total</th><th style="text-align:center">Fechados</th><th style="text-align:center">Abertos</th><th style="text-align:center">Conclusão</th></tr></thead><tbody>${devRows}</tbody></table></div>` : ""}
      ${projRows ? `<div class="sec"><div class="sec-title">Projetos (${projetos.length})</div><table><thead><tr><th>Projeto</th><th style="text-align:center">Tickets</th><th style="text-align:center">Fechados</th><th style="text-align:center">% Conc.</th><th>Deadline</th></tr></thead><tbody>${projRows}</tbody></table></div>` : ""}
      ${criticasRows ? `<div class="sec"><div class="sec-title">Tarefas Críticas — Atrasadas e Vencendo Hoje/Amanhã</div><table><thead><tr><th>Tarefa</th><th>Responsável</th><th>Status</th><th>Prazo</th></tr></thead><tbody>${criticasRows}</tbody></table></div>` : ""}
      <div class="footer">Redmine Sprint Dashboard · ${hoje}</div>
      <script>window.onload=()=>window.print()</script>
    </body></html>`;

    const win = window.open("", "_blank", "width=960,height=720");
    win.document.write(html);
    win.document.close();
  }

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={headerRow}>
        <div>
          <h1 style={pageTitle}>Dashboard Executivo</h1>
          <div style={pageSub}>Visão consolidada · Atualizado às {ts}</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={pdfBtn} onClick={exportarPDF}>📄 Exportar PDF</button>
          <button style={refreshBtn} onClick={() => load(true)}>↻ Atualizar</button>
        </div>
      </div>

      {/* ══ Seção Sprint ══════════════════════════════════════════════ */}
      <SectionTitle label="Sprint Atual" cor="#60a5fa" />
      <div style={kpiGrid(4)}>
        <KpiCard label="Tickets" value={sprintTotal} cor="#60a5fa" />
        <KpiCard label="Fechados" value={sprintFechado} cor="#4ade80" />
        <KpiCard label="Abertos" value={sprintAberto} cor="#fbbf24" />
        <KpiCard label="Conclusão" value={`${sprintPct}%`} cor={pctColor(sprintPct)} progress={sprintPct} />
      </div>

      {devsComAlerta.length > 0 && (
        <AlertBox cor="#fbbf24" icon="⚠">
          {devsComAlerta.length} desenvolvedor{devsComAlerta.length > 1 ? "es" : ""} com conclusão abaixo de 40%:{" "}
          <strong>{devsComAlerta.map(d => d.nome).join(", ")}</strong>
        </AlertBox>
      )}

      {/* ══ Seção Tarefas ═════════════════════════════════════════════ */}
      <div style={separador} />
      <SectionTitle label="Tarefas Internas" cor="#22d3ee" />
      <div style={notaInfo}>
        ℹ As tarefas abaixo são controles internos independentes da sprint — podem existir sem ticket ativo na sprint atual.
      </div>
      <div style={kpiGrid(5)}>
        <KpiCard label="Total" value={tarefas.length} cor="#60a5fa" />
        <KpiCard label="Abertas" value={tAbertas} cor="#fbbf24" />
        <KpiCard label="Concluídas" value={tConcluidas} cor="#4ade80" />
        <KpiCard label="Conclusão" value={`${tPct}%`} cor={pctColor(tPct)} progress={tPct} />
        <KpiCard label="Atrasadas" value={tAtrasadas} cor={tAtrasadas > 0 ? "#f87171" : "#4ade80"} destaque={tAtrasadas > 0} />
      </div>

      {(tAtrasadas > 0 || tHoje > 0 || tBloqueadas > 0 || tSemPrazo > 0) && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {tAtrasadas > 0 && <AlertBox cor="#f87171" icon="⚠" compact>{tAtrasadas} tarefa{tAtrasadas > 1 ? "s" : ""} atrasada{tAtrasadas > 1 ? "s" : ""}</AlertBox>}
          {tHoje > 0 && <AlertBox cor="#fb923c" icon="⏰" compact>{tHoje} vence{tHoje > 1 ? "m" : ""} hoje/amanhã</AlertBox>}
          {tBloqueadas > 0 && <AlertBox cor="#fb923c" icon="⛔" compact>{tBloqueadas} bloqueada{tBloqueadas > 1 ? "s" : ""}</AlertBox>}
          {tSemPrazo > 0 && <AlertBox cor="#64748b" icon="○" compact>{tSemPrazo} sem prazo definido</AlertBox>}
        </div>
      )}

      {/* ══ Seção Velocidade do Time ══════════════════════════════════ */}
      {velocidade.length > 0 && (
        <>
          <SectionTitle label="Velocidade do time para resolver tarefas" cor="#4ade80" />
          <div style={velGrid}>
            {velocidade.map(r => {
              const pct      = r.total > 0 ? Math.round(r.concluidas / r.total * 100) : 0;
              const onTimePct = r.concluidas > 0 ? Math.round(r.noprazo / r.concluidas * 100) : null;
              const cor      = r.atrasadas > 0 ? "#f87171" : pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#94a3b8";
              return (
                <div key={r.nome} style={velCard}>
                  <div style={velAvatar(cor)}>{r.nome.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{r.nome}</div>
                    <div style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: "#4ade80" }}>{r.concluidas} concluídas</span>
                      <span style={{ fontSize: 10, color: "#fbbf24" }}>{r.total - r.concluidas} abertas</span>
                      {r.atrasadas > 0 && <span style={{ fontSize: 10, color: "#f87171" }}>⚠ {r.atrasadas} atrasadas</span>}
                      {onTimePct !== null && <span style={{ fontSize: 10, color: "#22d3ee" }}>⏱ {onTimePct}% no prazo</span>}
                    </div>
                    <div style={{ marginTop: 5, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: cor, borderRadius: 2, transition: "width 0.5s" }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: cor, flexShrink: 0 }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══ Seção Projetos ════════════════════════════════════════════ */}
      <SectionTitle label="Projetos" cor="#a78bfa" />
      <div style={kpiGrid(3)}>
        <KpiCard label="Ativos" value={projAtivos} cor="#60a5fa" />
        <KpiCard label="Em Atraso / Parados" value={projEmAtraso} cor={projEmAtraso > 0 ? "#f87171" : "#4ade80"} destaque={projEmAtraso > 0} />
        <KpiCard label="Finalizados" value={projFinalizados} cor="#4ade80" />
      </div>

      <div style={projetosGrid}>
        {projetos.slice(0, 8).map(p => {
          const st      = gpStatus(p.id_gp_status);
          const fechados = p.tickets.filter(isTicketFechado).length;
          const total    = p.tickets.length;
          const pct      = total > 0 ? Math.round(fechados / total * 100) : 0;
          const hoje     = new Date(new Date().toDateString());
          const vencido  = p.deadline && new Date(p.deadline) < hoje;
          return (
            <div key={p.id} style={projetoCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: "#475569" }}>#{p.id}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", lineHeight: 1.3 }}>{p.descricao}</div>
                </div>
                <span style={{ ...statusPill(st.color), flexShrink: 0 }}>{st.label}</span>
              </div>
              {total > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#475569", marginBottom: 3 }}>
                    <span>{fechados}/{total} tickets</span>
                    <span style={{ color: pctColor(pct) }}>{pct}%</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: pctColor(pct), borderRadius: 3 }} />
                  </div>
                </div>
              )}
              {p.deadline && (
                <div style={{ fontSize: 10, marginTop: 6, color: vencido ? "#f87171" : "#475569" }}>
                  {vencido ? "⚠ " : "📅 "}Deadline: {p.deadline.slice(0, 10).split("-").reverse().join("/")}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

/* ─── Sub-componentes ─────────────────────────────────────────────── */

function SectionTitle({ label, cor }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
      <div style={{ width: 3, height: 18, borderRadius: 2, background: cor, flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: cor, textTransform: "uppercase", letterSpacing: 1 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: `${cor}22` }} />
    </div>
  );
}

function KpiCard({ label, value, cor, progress, destaque }) {
  return (
    <div style={kpiCard(destaque, cor)}>
      <div style={{ fontSize: 11, color: "#475569", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: cor, lineHeight: 1 }}>{value}</div>
      {progress !== undefined && (
        <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 6 }}>
          <div style={{ height: "100%", width: `${progress}%`, background: cor, borderRadius: 2, transition: "width 0.5s" }} />
        </div>
      )}
    </div>
  );
}

function AlertBox({ cor, icon, children, compact }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: compact ? "6px 12px" : "10px 14px",
      borderRadius: 9,
      background: `${cor}10`,
      border: `1px solid ${cor}33`,
      fontSize: compact ? 11 : 12,
      color: cor,
      fontWeight: 600,
      flex: compact ? "none" : 1,
    }}>
      <span>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page = { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 };

const headerRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };

const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };

const pageSub = { color: "#475569", fontSize: 13, marginTop: 4 };

const refreshBtn = {
  padding: "8px 14px", borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.05)",
  color: "#60a5fa", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const pdfBtn = {
  padding: "8px 14px", borderRadius: 9,
  border: "1px solid rgba(74,222,128,0.25)", background: "rgba(74,222,128,0.06)",
  color: "#4ade80", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const kpiGrid = n => ({
  display: "grid", gridTemplateColumns: `repeat(${n}, 1fr)`, gap: 12,
});

const kpiCard = (destaque, cor) => ({
  background: destaque ? `${cor}10` : "rgba(11,22,42,0.6)",
  border: `1px solid ${destaque ? cor + "33" : "rgba(96,165,250,0.08)"}`,
  borderRadius: 12, padding: "14px 16px",
  boxShadow: destaque ? `0 0 12px ${cor}18` : "none",
});

const projetosGrid = {
  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12,
};

const projetoCard = {
  background: "rgba(11,22,42,0.6)",
  border: "1px solid rgba(96,165,250,0.08)",
  borderRadius: 12, padding: "14px 16px",
};

const statusPill = cor => ({
  fontSize: 10, fontWeight: 700, color: cor,
  background: `${cor}18`, border: `1px solid ${cor}33`,
  borderRadius: 6, padding: "2px 7px", whiteSpace: "nowrap",
});

const separador = {
  borderTop: "1px solid rgba(96,165,250,0.08)",
  marginTop: 4,
};

const notaInfo = {
  fontSize: 11, color: "#475569",
  background: "rgba(34,211,238,0.04)",
  border: "1px solid rgba(34,211,238,0.1)",
  borderRadius: 8, padding: "8px 12px",
};

const velGrid = { display: "flex", flexDirection: "column", gap: 8 };

const velCard = {
  display: "flex", alignItems: "center", gap: 14,
  background: "rgba(11,22,42,0.6)",
  border: "1px solid rgba(96,165,250,0.08)",
  borderRadius: 12, padding: "12px 16px",
};

const velAvatar = cor => ({
  width: 36, height: 36, borderRadius: "50%",
  background: `${cor}22`, border: `2px solid ${cor}55`,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 15, fontWeight: 800, color: cor, flexShrink: 0,
});

const skeletonPage = { display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" };
